import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Clock, 
  Users, 
  Coffee, 
  Music, 
  Target,
  AlertTriangle,
  MessageCircle,
  ExternalLink,
  BookOpen,
  Sparkles,
  ArrowRight,
  Calendar,
  TrendingDown,
  Search,
  Video,
  Timer,
  CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";

const GrooveHistory = () => {

  const features = [
    { icon: Users, text: "Small groups of 4 for intimate coworking sessions" },
    { icon: Clock, text: "Structured hour-long sessions with intro, work, and wrap-up" },
    { icon: Video, text: "Camera-on check-ins, camera-off deep work" },
    { icon: Coffee, text: "Virtual lobby matching with strangers or friends" },
    { icon: Timer, text: "Pomodoro-style 50-minute focused work blocks" },
    { icon: Heart, text: "Regular 'squads' that became virtual coworkers" },
  ];

  const alternatives = [
    { name: "Focusmate", url: "/online-study/focusmate/", description: "Virtual coworking sessions", internal: true },
    { name: "StudyStream", url: "/online-study/studystream/", description: "Live study rooms", internal: true },
    { name: "Study Together", url: "/online-study/studytogether/", description: "Discord-based community", internal: true },
    { name: "Fiveable", url: "/online-study/fiveable/", description: "AP exam prep platform", internal: true },
    { name: "Quizlet", url: "/online-study/quizlet/", description: "Flashcard-based learning", internal: true },
  ];

  const cswFeatures = [
    "Free virtual study rooms",
    "No time limits",
    "No paywalls",
    "Integrated music and media",
    "Focus timers and task tools",
    "Study + work hybrid spaces",
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/groove/history/"
    },
    "headline": "Groove: The Rise and Fall of a Virtual Coworking Community",
    "alternativeHeadline": "How Groove Created Magic for Remote Workers—And Why It Couldn't Survive",
    "description": "This article recounts the rise and fall of Groove, a pandemic-era virtual coworking app that created a movement of 'Groovers' beating procrastination and loneliness together. It explores the platform's structured sessions, devoted community, sudden 2025 shutdown, and the alternatives that carry its legacy forward.",
    "keywords": "Groove app, virtual coworking, Focusmate alternative, Flow Club, Caveday, body doubling, remote work productivity, CSW, Chill Study Work, coworking shutdown",
    "articleSection": "Technology, Productivity",
    "genre": ["Technology", "Productivity", "Blog"],
    "inLanguage": "en",
    "wordCount": 2500,
    "about": ["Groove coworking app", "virtual coworking platforms", "remote work productivity"],
    "author": {
      "@type": "Organization",
      "name": "CSW Team",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Chill Study Work",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/og-image.png",
        "width": 1200,
        "height": 630
      }
    },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11",
    "image": {
      "@type": "ImageObject",
      "url": "https://csw.live/og-image.png",
      "width": 1200,
      "height": 630
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Groove History - The Rise and Fall of Virtual Coworking"
        description="A retrospective on Groove - the pandemic-era coworking app that created a movement of 'Groovers' beating procrastination together, and why it shut down in 2025."
        keywords="groove app history, groove coworking shutdown, virtual coworking community, focusmate alternative, flow club, body doubling app"
        canonicalUrl="/groove/history/"
        metaImageDescription="groove coworking"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_50%)]" />
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li>/</li>
                <li><Link to="/groove/" className="hover:text-primary transition-colors">Groove</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2020-2025
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Groove Story
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                When work-from-home left freelancers feeling isolated, Groove stepped up as a beacon of solidarity.
              </p>
              
              <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto">
                For a while, it worked brilliantly. Then it was <span className="text-foreground font-medium">gone</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    Groove wasn't just another productivity app; it created a <span className="text-foreground font-semibold">movement</span> of people "grooving" together in structured coworking sessions to beat procrastination and loneliness.
                  </p>
                  <p className="text-lg leading-relaxed text-muted-foreground mt-4">
                    Users spoke of Groove with reverence—<span className="italic">Fast Company</span> profiled it as a favorite among solo entrepreneurs who said it helped them stave off isolation. Yet, despite this loyal following, Groove shockingly shut down in mid-2025. <Link to="/groove/" className="text-primary hover:underline font-medium">Looking for a Groove alternative?</Link>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* History & Founders */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">History & Founders</h2>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Groove got its start in <span className="text-foreground font-semibold">2020</span> at the height of COVID-19 lockdowns. CEO Josh Greene and his co-founders—feeling lonely themselves—dreamed up the app as a virtual coworking space that could capture the experience of sitting alongside colleagues in a coffee shop.
                </p>
                
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  They settled on a structure where users meet strangers or acquaintances in a virtual lobby and join a group of up to four for a session that includes:
                </p>

                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  <Card className="border-border/50">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-primary mb-2">5 min</div>
                      <p className="text-sm text-muted-foreground">Introductory chat</p>
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-primary mb-2">50 min</div>
                      <p className="text-sm text-muted-foreground">Cameras-off deep work</p>
                    </CardContent>
                  </Card>
                  <Card className="border-border/50">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-primary mb-2">5 min</div>
                      <p className="text-sm text-muted-foreground">Wrap-up debrief</p>
                    </CardContent>
                  </Card>
                </div>

                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Groove emerged from beta in <span className="text-foreground font-semibold">January 2024</span> with a pricing model of US$18 per month or US$120 per year. Between September and November 2023, users logged more than <span className="text-foreground font-semibold">30,700 sessions</span>, totaling over 1.5 million minutes of "Grooving."
                </p>

                <div className="flex flex-wrap gap-3 mb-8">
                  <a 
                    href="https://www.fastcompany.com/91014739/groove-an-app-that-lets-people-find-coworking-partners-emerges-from-beta" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Fast Company Profile
                  </a>
                  <a 
                    href="https://medium.com/groove-with-us/pioneering-a-new-way-for-groovers-to-support-each-other-5fafb0fd794f" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Founder's Medium Post
                  </a>
                </div>

                <p className="text-xl text-foreground font-medium italic border-l-4 border-primary pl-6">
                  The format echoed the Pomodoro technique and offered both accountability and social connection.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features & Community Appeal */}
        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Features & Community Appeal</h2>
              </div>
              
              <p className="text-muted-foreground text-lg mb-8">
                Groove's magic lay in its <span className="text-foreground font-medium">simplicity and ritual</span>:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {features.map((feature, index) => (
                  <Card key={index} className="border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  The hour-long sessions forced users to articulate their goals, work quietly under a timer, and then debrief—an approach many found <span className="text-foreground font-medium">addictive</span>.
                </p>
                <p>
                  People announced, <span className="italic text-foreground">"I will finish drafting this report in the next hour,"</span> to their small group and hustled to make it happen. Over time, they formed regular squads that met daily, becoming virtual "coworkers" and friends.
                </p>

                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl mb-4">
                      Product-Market Fit Survey Results (2023):
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-primary">95%</div>
                        <p className="text-muted-foreground text-sm">would be disappointed if Groove disappeared</p>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-primary">43%</div>
                        <p className="text-muted-foreground text-sm">would be <span className="font-semibold">very</span> disappointed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <p>
                  Groove was intentionally small—often marketed as a <span className="italic">"global movement of people journeying through life together"</span>—and membership required filling out an application detailing one's goals. This curated atmosphere made the platform feel like a supportive clubhouse.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Shutdown */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">The Shutdown</h2>
              </div>

              <div className="relative pl-8 border-l-2 border-destructive/30 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive" />
                  <p className="text-lg font-medium text-foreground">Mid-2025</p>
                  <p className="text-muted-foreground mt-2">
                    Groove quietly wound down. The company never issued a detailed public post-mortem.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/60" />
                  <p className="text-muted-foreground">
                    Growth guru Lex Roman noted in a May 2025 blog post that her group still used Groove for coworking but that <span className="italic">"Groove is winding down soon."</span>
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/40" />
                  <p className="text-muted-foreground">
                    By summer 2025, attempts to log in were met with a shutdown notice thanking users and pointing them to other resources.
                  </p>
                </div>
              </div>

              <div className="mt-10 space-y-6 text-lg text-muted-foreground">
                <p className="font-medium text-foreground">Observers pointed to several possible reasons:</p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {[
                    "User base remained relatively small despite devotion",
                    "Converting free beta users to paying subscribers proved challenging",
                    "Maintaining video infrastructure for global sessions was costly",
                    "Competitive landscape tightened—Focusmate, Flow Club growing",
                    "Big players (Zoom, Teams) adding focus-session features",
                  ].map((item, index) => (
                    <Card key={index} className="border-border/50">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-destructive shrink-0" />
                        <p className="text-muted-foreground text-sm">{item}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      Without sufficient scale or revenue, Groove simply couldn't survive.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Community Reaction */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <MessageCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Community Reaction & Impact</h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                For the tight-knit Groove community, the shutdown felt <span className="text-foreground font-medium">personal</span>.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {[
                  "Yet another great app gone.",
                  "Groove was a friend who got me out of bed.",
                  "I built daily habits and friendships through this platform.",
                  "The abruptness frustrated people who invested months into the community.",
                ].map((quote, index) => (
                  <Card key={index} className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                    <CardContent className="p-5">
                      <p className="text-foreground italic">"{quote}"</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  The survey results—<span className="text-foreground font-semibold">95% would be disappointed if Groove vanished</span>—hinted at how deeply integrated the app was into users' routines.
                </p>

                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      In response, many Groovers migrated together to alternative platforms or set up unofficial coworking channels on Discord or Slack.
                    </p>
                    <p className="text-muted-foreground mt-2 italic">
                      They were determined to preserve the accountability and friendships they valued.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What Remains */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">What Remains</h2>
              <p className="text-lg text-muted-foreground mb-6">
                With Groove gone, its website and app are no longer accessible. The founders have not open-sourced the code or provided an archive.
              </p>
              <p className="text-xl text-foreground font-medium">
                All that remains is the legacy and the habits it instilled.
              </p>
              <p className="text-muted-foreground mt-4">
                Some former users regrouped in unofficial channels to continue scheduling peer coworking sessions, effectively keeping the Groove vibe alive without the app. Others have found new homes in <Link to="/groove/" className="text-primary hover:underline font-medium">platforms like CSW that carry forward Groove's spirit</Link>.
              </p>
              <p className="text-muted-foreground mt-4 italic">
                In blog reflections, people praised Groove for teaching them the value of intentional structure and external accountability—habits they now maintain on their own.
              </p>
            </div>
          </div>
        </section>

        {/* Alternatives & Successors */}
        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Search className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Alternatives & Successors</h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                The disappearance of Groove left many users searching for a new virtual coworking home. Several platforms offer similar body-doubling experiences:
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    name: "Focusmate",
                    description: "Virtual coworking service with 25-, 50- or 75-minute 1:1 sessions. 12M+ completed sessions, 500M+ minutes of focused work, members from 150 countries. Free plan offers 3 sessions/week; Plus is $6.99/month (annual).",
                    url: "/online-study/focusmate/",
                    internal: true
                  },
                  {
                    name: "Flow Club",
                    description: "Host-led platform with choreographed sessions: share goals, work to music, debrief and celebrate. Memberships start at $33.33/month with free trial available.",
                    url: "https://www.flow.club/",
                    internal: false
                  },
                  {
                    name: "Caveday",
                    description: "Facilitated 'Cave' sessions with deep-work sprints, energizing breaks, and community support. Basic ($18/month) to Unlimited ($35/month) tiers available.",
                    url: "https://www.caveday.org/",
                    internal: false
                  },
                  {
                    name: "Flown",
                    description: "UK-based platform with Flocks, Portholes, Airflow sessions led by facilitators. Memberships start at $19/month with 30-day free trial.",
                    url: "https://www.flown.com/",
                    internal: false
                  },
                ].map((alt, index) => (
                  alt.internal ? (
                    <Link key={index} to={alt.url} className="block group">
                      <Card className="border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                              {alt.name}
                            </h3>
                            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <p className="text-muted-foreground">{alt.description}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <a key={index} href={alt.url} target="_blank" rel="noopener noreferrer" className="block group">
                      <Card className="border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                              {alt.name}
                            </h3>
                            <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <p className="text-muted-foreground">{alt.description}</p>
                        </CardContent>
                      </Card>
                    </a>
                  )
                ))}
              </div>

              <p className="text-lg text-muted-foreground mb-4">
                Other options include <span className="text-foreground font-medium">Cofocus</span> (free 50-minute 1:1 sessions), <span className="text-foreground font-medium">Focus Sessions</span> ($47/month for unlimited 90-minute facilitated sessions), and Discord-based communities like <span className="text-foreground font-medium">Study Together</span>.
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {alternatives.map((alt, index) => (
                  <Link 
                    key={index}
                    to={alt.url}
                    className="group"
                  >
                    <Card className="h-full border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {alt.name}
                          </h3>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <p className="text-sm text-muted-foreground">{alt.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Where CSW Comes In */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/20">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Where CSW Comes In</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  CSW — <span className="text-foreground font-semibold">Chill Study Work</span> — provides a free virtual study and productivity platform that carries forward the spirit of what Groove created. <Link to="/groove/" className="text-primary hover:underline font-medium">See how CSW compares to Groove →</Link>
                </p>
                
                <p className="text-xl text-foreground font-medium">
                  24/7 study rooms. Unlimited streams. No hidden fees.
                </p>

                <a 
                  href="https://app.csw.live" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                >
                  Try CSW Now
                  <ExternalLink className="w-4 h-4" />
                </a>

                <p className="font-medium text-foreground mt-8">CSW offers:</p>

                <div className="grid md:grid-cols-2 gap-3">
                  {cswFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <Card className="border-primary/30 bg-primary/5 mt-8">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      Like Groove, CSW understands a core truth:
                    </p>
                    <p className="text-2xl font-bold text-primary mt-3">
                      Productivity isn't about grinding alone. It's about structured presence.
                    </p>
                  </CardContent>
                </Card>

                {/* Internal CSW Links */}
                <div className="mt-8 pt-8 border-t border-border/50">
                  <p className="font-medium text-foreground mb-4">Explore CSW Features:</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Link to="/study-stream/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study Stream
                    </Link>
                    <Link to="/study-timer/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study Timer
                    </Link>
                    <Link to="/task-management/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Task Management
                    </Link>
                    <Link to="/study-with-me/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study With Me
                    </Link>
                    <Link to="/group-study/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Group Study
                    </Link>
                    <Link to="/study-with-friends/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study With Friends
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Conclusion</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p className="text-xl text-foreground">
                  Groove's story is a reminder that even beloved online communities can vanish overnight.
                </p>
                
                <p>
                  The app delivered a simple yet powerful recipe—<span className="text-foreground font-medium">structure, accountability, and human connection</span>—that resonated deeply during a time of isolation.
                </p>

                <p>
                  Its sudden shutdown disappointed a loyal user base and highlighted the fragility of relying on platforms without sustainable revenue models.
                </p>

                <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 mt-8">
                  <CardContent className="p-8 text-center">
                    <p className="text-xl text-foreground font-medium mb-4">
                      But the appetite for virtual coworking persists.
                    </p>
                    <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-4">
                      Services like Focusmate, Flow Club, and CSW carry the torch.
                    </p>
                    <Link to="/groove/" className="inline-flex items-center gap-2 text-primary hover:underline font-medium">
                      Discover CSW as a Groove Alternative <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>

                <p className="text-center text-xl mt-8">
                  For former Groovers, the challenge now is to recreate the sense of community they once had—whether in new platforms or in homegrown spaces built with their friends.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sources */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Sources</h2>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  <a href="https://www.fastcompany.com/91014739/groove-an-app-that-lets-people-find-coworking-partners-emerges-from-beta" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Steven Melendez, Fast Company – "Groove, an app that lets people find coworking partners, emerges from beta," Jan 23 2024
                  </a>
                </p>
                <p>
                  <a href="https://medium.com/groove-with-us/pioneering-a-new-way-for-groovers-to-support-each-other-5fafb0fd794f" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Joshua Greene, Medium – "Pioneering a new way for Groovers to support each other," Mar 15 2023
                  </a>
                </p>
                <p>
                  <a href="https://www.revenuerulebreaker.com/i-shut-down-my-membership-whats-emerged-from-its-shell-has-been-so-much-better/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Lex Roman, Revenue Rulebreaker – "Want to start a community? Here's how I turned mine around," May 23 2025
                  </a>
                </p>
                <p>
                  <a href="https://www.litespace.io/blog/the-top-virtual-coworking-space-platforms-in-2025" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Litespace – The Top Virtual Coworking Space Platforms in 2025
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 text-center">Related Reading</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/blog/science-behind-study-with-me/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        The Science Behind Study With Me
                      </h3>
                      <p className="text-sm text-muted-foreground">Why studying together works for focus and accountability.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        How Turning On My Camera Boosted My Productivity
                      </h3>
                      <p className="text-sm text-muted-foreground">The unexpected benefits of camera-on study sessions.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/benefits-of-online-learning-community/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        Benefits of Online Learning Communities
                      </h3>
                      <p className="text-sm text-muted-foreground">How virtual study groups enhance learning outcomes.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/studyverse/history/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        The Studyverse Story
                      </h3>
                      <p className="text-sm text-muted-foreground">Another beloved study platform that shut down—and its legacy.</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Find Your Coworking Community?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join CSW and experience the same structured productivity that made Groove special — for free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="gap-2" asChild>
                  <Link to="/online-study/focusmate/">
                    Compare Alternatives
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="gap-2" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Try CSW Now
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GrooveHistory;
